import EventEmitter from 'eventemitter3';
import SignalChannel from '../../signal/signal-channel';
import { Room } from '../../room';
import { ConnectionEvent, ConnectionState, DTLS_STATE_UNKNOWN, ErrorCode, ErrorName, IRemoteTrack, IRoom, Logger, MULTI_VIDEO_DATA_TYPE, NAME, PEERCONNECTION_CONNECTING_TIMEOUT, PEER_CONNECTION_STATE, RemoteStreamType, RoomEvent, RtcError, SSRC, TRANSPORT_DIRECTION, VideoCodec, VideoContentHint, getRetryCount, loggerManager, SSRCGroup, checkPendingPromise, clearPendingPromise, CheckPendingPromiseType } from 'trtc-js-sdk-core';
import { AbilityVideoCodec, ClientAbility, ServerAbility, getAnswerSDPFromServerAbility, getClientAbility, getUplinkSSRCFromSDP, getVideoMediaConfig, handleOfferSDP, initCodecConfig, getH264ProfileByProfileLevelId, convertProfileLevelIdToHex, extractProfileLevelIdFromFmtp, patchFmtpProfileLevelId } from './sdp';
import { ChannelEvent, SignalCommand, SignalEvent } from '../../signal/constant';
import { delay, getReconnectionTimeout, isUndefined, performanceNow, setNetworkTypeFromWebRTC } from 'trtc-js-sdk-core/src/utils/utils';
import { IS_INSERTABLE_STREAM_SUPPORTED, IS_SCRIPT_TRANSFORM_SUPPORTED, isGetReceiversSupported, isGetSendersSupported, isSelectedCandidatePair, isSetParametersSupported } from 'trtc-js-sdk-core/src/common/rtc-detection';
import innerEmitter from 'trtc-js-sdk-core/src/inner-emitter/inner-emitter';
import INNER_EVENT from 'trtc-js-sdk-core/src/inner-emitter/inner-event';
import { generateErrorMessage } from 'trtc-js-sdk-core/src/common/error-generator';
import { addCallQueue, clearCallQueue, addCallQueueValidArgs } from 'trtc-js-sdk-core/src/utils/decorators/api-call-queue';
import { parse, write } from '../sdp';
import transform, { SharedAttributes } from 'sdp-transform';
import Connection from '../connection';
import { CHROME_MAJOR_VERSION, FIREFOX_MAJOR_VERSION, IOS_MAIN_VERSION, IOS_VERSION, IS_CHROMIUM_BASE, IS_FIREFOX, IS_IOS, IS_WECHAT, isAMDGPU, isVersionLargerThan, isVersionSmallerThan, SAFARI_VERSION } from 'trtc-js-sdk-core/src/utils/environment';
import kvStatManager, { KV_REPORT_KEY_NETWORK } from 'trtc-js-sdk-core/src/manager/kv-stat-manager';
import { isBoolean, isFunction, isString } from 'trtc-js-sdk-core/src/utils/type-check';
import { HealthDetectorEvent } from '../../metrics/health-detector';
import { createScriptTransformWorker } from '../../script-transform/script-transform';
import NetworkQuality from '../../metrics/network-quality';
import { reportSuccessRate } from 'trtc-js-sdk-core/src/utils/decorators/report-success-rate';

export const enum SPC_EVENT {
  TRACK = 'track',
  DATA_CHANNEL_MESSAGE = 'data_channel_msg',
  CONNECTION_STATE_CHANGED = ConnectionEvent.CONNECTION_STATE_CHANGED,
  FIREWALL_RESTRICTION = ConnectionEvent.FIREWALL_RESTRICTION,
  RECONNECTED = 'spc-reconnected',
  RECONNECT_FAILED = 'spc-reconnect-failed',
  ERROR = 'error',
  SEI_MESSAGE = 'sei-message',
  DUMP = 'dump'
}

interface SPC_EVENT_TYPE {
  [SPC_EVENT.TRACK]: [event: RTCTrackEvent]
  [SPC_EVENT.DATA_CHANNEL_MESSAGE]: [{ data: DataChannelMessage }]
  [SPC_EVENT.CONNECTION_STATE_CHANGED]: [{ prevState: ConnectionState, state: ConnectionState }]
  [SPC_EVENT.FIREWALL_RESTRICTION]: []
  [SPC_EVENT.RECONNECTED]: [void]
  [SPC_EVENT.ERROR]: [error?: RtcError | Error]
  [SPC_EVENT.SEI_MESSAGE]: [{ data: ArrayBuffer, seiPayloadType: number, userId: string, streamType: RemoteStreamType }],
  [SPC_EVENT.DUMP]: [{ data: ArrayBuffer, userId: string, streamType: RemoteStreamType, isAudio: boolean }]
}

interface SEI_WORKER {
  worker: Worker
  messageChannel: MessageChannel,
}

let seq = 1;

export default class SinglePeerConnection extends EventEmitter<SPC_EVENT_TYPE> {
  stat = {
    iceStartTime: 0,
    iceEndTime: 0,
    dtlsStartTime: 0,
    dtlsEndTime: 0,
    peerConnectionStartTime: 0,
    peerConnectionEndTime: 0
  };
  isDestroyed = false;
  currentState: ConnectionState = ConnectionState.DISCONNECTED;
  private _room: Room;
  private _signalChannel: SignalChannel | null = null;

  get signalChannelInstance(): SignalChannel {
    return this._signalChannel ?? this._room.signalChannel!;
  }
  private _peerConnection: RTCPeerConnection | null = null;
  private _datachannel: RTCDataChannel | null = null;
  private _enableDataChannel: boolean;
  private _enableDtx: boolean | undefined;
  private _log: Logger;
  private _downlinkMIDMap: Map<string, number[]> = new Map(); // Map<userId, [0,1,2....]>
  private _downlinkMIDUserIDMap: Map<number, string> = new Map();
  private _reconnectionTimer = -1;
  reconnectionCount = 0;
  clientAbility: ClientAbility;
  private _serverAbility: ServerAbility | null = null;
  // 用于同时订阅多路下行时，减少 sdp 更新的次数。优化性能。
  addDownlinkQueue = new Set();
  // 用于同时取消订阅多路下行时，减少 sdp 更新的次数。优化性能。
  removeDownlinkQueue = new Set();
  private _parsedAnswer: transform.SessionDescription | null = null;
  // 懒加载获取 parsedAnswer，如果没有则从 remoteDescription 解析
  get parsedAnswer(): transform.SessionDescription | null {
    if (!this._parsedAnswer && this._peerConnection?.remoteDescription?.sdp) {
      this._parsedAnswer = parse(this._peerConnection.remoteDescription.sdp);
    }
    return this._parsedAnswer;
  }
  // 更新 sdp 的 promise 实例
  private _updateSDPPromise: Promise<void> | null = null;
  private _waitForPCConnectedPromise?: Promise<void> | null;
  private clearWaitForConnectedPromise?: () => void;
  private clearConnectTimeout?: () => void;
  private _isSDPLogged = false;
  enableInsertableStreams = false;
  insertableStreamsAbortMap = new Map<RTCRtpSender | RTCRtpReceiver, AbortController>();
  receiverRemoteTrackMap = new WeakMap<RTCRtpReceiver, IRemoteTrack>();
  scriptTransformWorker?: Worker;

  private _isRelayTried = false;
  private _rttOverCount = 0;
  private originOffer: RTCSessionDescriptionInit | null = null;

  autoSubscribedSsrcGroups: Map<string, SSRCGroup[]> = new Map(); // key 是房间号, value 是 switchRoom 发给后台的 ssrcGroup
  autoSubscribedUserMap: Map<string, { srcTinyId: string, userId: string, groupIndex: number }> = new Map(); // 后台返回已订阅用户在 ssrcGroups 中对应的索引
  private _h265DecodeFailed = false;
  // 与后台协商后，是否支持 h264 编码
  // 是否支持 h264 编码：本地支持 && 后台协商支持
  get isH264EncodeSupported() {
    let result = this._room.checkSystemResult.detail.isH264EncodeSupported;
    if (this._serverAbility) {
      result = result && !!this._serverAbility.video.codecs.find(item => item.codec.toLowerCase() === 'h264');
    }
    return result;
  }
  addAbortController(key: RTCRtpSender | RTCRtpReceiver, abortController: AbortController) {
    this.insertableStreamsAbortMap.get(key)?.abort('destroy');
    this.insertableStreamsAbortMap.set(key, abortController);
  }
  // 是否支持 vp8 编码：本地支持 && 后台协商支持
  get isVP8EncodeSupported() {
    let result = this._room.checkSystemResult.detail.isVp8EncodeSupported;
    // 若后台配置了 vp8 白名单，则在 ability 中会有 vp8
    if (this._serverAbility) {
      result = result && this._serverAbility.video.codecs.find(item => item.codec.toLowerCase() === VideoCodec.VP8);
    }
    return result;
  }

  get isH265EncodeSupported() {
    let result = this._room.checkSystemResult.detail.isH265EncodeSupported;
    if (this._serverAbility) {
      result = result && !!this._serverAbility.video.codecs.find(item => item.codec.toLowerCase() === 'h265');
    }
    return result;
  }

  get videoCodec() {
    const codec = this._parsedAnswer?.media[1].rtp.find(item => [VideoCodec.H264, VideoCodec.VP8, VideoCodec.H265].includes(item.codec.toLowerCase() as VideoCodec));
    if (codec) return codec.codec.toLowerCase() as VideoCodec;
    // 后台下发优先使用h265，回退后使用 _parsedAnswer 中的第一个编码
    if (this._serverAbility?.useH265) {
      return VideoCodec.H265;
    }
    return this._serverAbility?.useVp8 ? VideoCodec.VP8 : VideoCodec.H264;
  }
  get downlinkVideoCodec() {
    // FIXME: 由于下行同时存在H265 H264两种格式，这里获取下行视频的编码格式不准确，通过download connection videoCodec获取实际的编码格式
    if (this._serverAbility?.useH265 && this._serverAbility?.video.decoders.find(item => item.codec.toLowerCase() === 'h265') && !this._h265DecodeFailed) {
      return VideoCodec.H265;
    }
    return this._serverAbility?.video.decoders.find(item => item.codec.toLowerCase() === 'h264') ? VideoCodec.H264 : VideoCodec.VP8;
  }

  get isUsingH264() {
    return this.videoCodec === VideoCodec.H264;
  }
  get isUsingH265() {
    return this.videoCodec === VideoCodec.H265;
  }
  get isUsingVP8() {
    return this.videoCodec === VideoCodec.VP8;
  }
  get is42001fSupported() {
    if (!this.clientAbility) return false;
    return !!this.clientAbility.video.codecs.find(item => item.fmtp.includes('42001f'));
  }
  /**
   * 检查是否支持指定的 H.264 Profile Level ID
   * @param profileLevelId - H.264 Profile Level ID，如 '42001f', '42e01f' 等
   * @returns 是否支持该 Profile Level ID
   */
  isProfileLevelIdSupported(profileLevelId: string) {
    if (!this.clientAbility) return false;
    return !!this.clientAbility.video.codecs.find(item => item.fmtp.includes(profileLevelId));
  }

  get uplinkSSRC() {
    if (this._peerConnection && this._peerConnection.localDescription) {
      return getUplinkSSRCFromSDP(this._peerConnection.localDescription.sdp);
    }
    return {
      audioSsrc: 0,
      audioRtxSsrc: 0,
      bigVideoSsrc: 0,
      bigVideoRtxSsrc: 0,
      smallVideoSsrc: 0,
      smallVideoRtxSsrc: 0,
      auxVideoSsrc: 0,
      auxVideoRtxSsrc: 0
    };
  }
  constructor({ signalChannel, room, enableDataChannel, enableDtx }: { signalChannel?: SignalChannel, room: Room, enableDataChannel: boolean, enableDtx?: boolean }) {
    super();
    this._room = room;
    this._enableDataChannel = enableDataChannel;
    this._enableDtx = enableDtx;
    this._signalChannel = signalChannel ?? null;
    this._log = loggerManager.createLogger({
      parent: this._room.getLogger(),
      id: `spc${seq++}`,
      userId: this._room.userId,
      sdkAppId: this._room.sdkAppId
    });
    if (this._room.enableCodecPipeline) {
      if (IS_INSERTABLE_STREAM_SUPPORTED) {
        this.enableInsertableStreams = true;
      } else {
        this.initScriptTransformWorker();
      }
    }
    this._room.healthDetector.on(HealthDetectorEvent.BAD_HEALTH, this.onBadHealth, this);
  }

  getOriginOffer() {
    return this.originOffer;
  }

  onBadHealth({ isAux }: { isAux: boolean; }) {
    // 暂时禁用切回软编的逻辑，原因是检测 bad health 的逻辑不可靠
    // this.useHWEncoder(false, isAux ? MULTI_VIDEO_DATA_TYPE.MULTI_DATA_AUX_IMG : MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG);
  }

  initScriptTransformWorker() {
    if (IS_SCRIPT_TRANSFORM_SUPPORTED) {
      this.scriptTransformWorker = createScriptTransformWorker({
        videoEncodePipeline: this._room.videoManager.encodePipeline,
        videoDecodePipeline: this._room.videoManager.decodePipeline,
        audioEncodePipeline: this._room.audioManager.encodePipeline,
        audioDecodePipeline: this._room.audioManager.decodePipeline
      });
      this.scriptTransformWorker.onmessage = event => {
        if (event.data.type === 'sei') {
          this.emit(SPC_EVENT.SEI_MESSAGE, event.data);
        } else if (event.data.type === 'audio-ntp') {
          // TODO: 更新 RemoteAudioTrack 的 ntp 时间戳。
        }
        if (event.data.type === 'dump') {
          this.emit(SPC_EVENT.DUMP, event.data);
        }
      };
      this.scriptTransformWorker.onerror = event => {
        this._log.error('scriptTransformWorker error: ', event.message);
      };
    }
  }

  get isReconnecting() {
    return this.currentState === ConnectionState.RECONNECTING || this._reconnectionTimer > 0 || this.reconnectionCount > 0;
  }

  // 获取 dtlsTransport 实例， max-bundle 一个 pc 对应一个 dtlsTransport 实例
  get dtlsTransport() {
    if (!this._peerConnection) return null;
    const senders = this._peerConnection.getSenders();
    if (senders.length === 0) return null;
    return senders[0].transport;
  }

  getPeerConnectionConfig(iceServers?: RTCIceServer[]): RTCConfiguration {
    const config = {
      encodedInsertableStreams: this.enableInsertableStreams,
      // 规避更新 sdp 后，编码器重启的问题。
      offerExtmapAllowMixed: true,
      iceServers,
      iceTransportPolicy: this._room.getIceTransportPolicy(),
      sdpSemantics: this._room.sdpSemantics,
      bundlePolicy: 'max-bundle' as RTCBundlePolicy,
      rtcpMuxPolicy: 'require' as RTCRtcpMuxPolicy,
      // the two items below are non-standard & obsolete
      tcpCandidatePolicy: 'disable',
      IceTransportsType: 'nohost' as RTCIceTransportPolicy
    };

    // encodedInsertableStreams 的配置不能动态更新，否则会报错。
    // https://chromium.googlesource.com/chromium/src/third_party/+/master/blink/renderer/modules/peerconnection/rtc_peer_connection.cc#1321
    // @ts-ignore
    const prevEncodedInsertableStreams = this._peerConnection?.getConfiguration().encodedInsertableStreams;
    if (isBoolean(prevEncodedInsertableStreams)) {
      config.encodedInsertableStreams = prevEncodedInsertableStreams;
    }
    this._log.debug('getPeerConnectionConfig', JSON.stringify(config));
    return config;
  }

  async initialize(iceServers?: RTCIceServer[]) {
    let offer;
    try {
      this._peerConnection = new RTCPeerConnection(this.getPeerConnectionConfig(iceServers));
      this._peerConnection.oniceconnectionstatechange = () => {
        if (!this._peerConnection) return;
        const iceState = this._peerConnection.iceConnectionState;
        this._log.debug(`ice state: ${iceState}`);
        if (iceState === 'checking' && this.stat.iceStartTime === 0) {
          this.stat.iceStartTime = Date.now();
        } else if (iceState === 'connected' && this.stat.iceEndTime === 0) {
          this.stat.iceEndTime = Date.now();
          this.signalChannelInstance?.clearBakRelayIps();
          kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_NETWORK.ICE_CONNECT, cost: this.stat.iceEndTime - this.stat.iceStartTime });
        } else if (iceState === 'failed') {
          kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.ICE_CONNECT });
        }
      };
      this._peerConnection.onsignalingstatechange = () => {
        const signalingState = this._peerConnection?.signalingState || '';
        this._log[signalingState === 'closed' ? 'debug' : 'info'](`signaling state: ${signalingState}`);
      };
      this._peerConnection.onconnectionstatechange = this.onConnectionStateChange.bind(this);
      this._peerConnection.ontrack = event => this.emit(SPC_EVENT.TRACK, event);

      if (this._enableDataChannel) {
        this._datachannel = this._peerConnection.createDataChannel(`${this._room.userId}dc`);
        this._datachannel.binaryType = 'arraybuffer';
        this._datachannel.onopen = () => {
          this._log.info('datachannel open');
        };
        this._datachannel.onclose = () => {
          this._log.info('datachannel close');
        };
        this._datachannel.onmessage = event => {
          const message = new DataChannelMessage(event.data);
          this.emit(SPC_EVENT.DATA_CHANNEL_MESSAGE, { data: message });
        };
        this._datachannel.onerror = error => {
          this._log.warn('datachannel error', error);
        };
      }

      this._peerConnection.addTransceiver(NAME.AUDIO, {
        direction: NAME.TRANSCEIVER_DIRECTION_SENDONLY
      });
      this._peerConnection.addTransceiver(NAME.VIDEO, {
        direction: NAME.TRANSCEIVER_DIRECTION_SENDONLY
      });
      this._peerConnection.addTransceiver(NAME.VIDEO, {
        direction: NAME.TRANSCEIVER_DIRECTION_SENDONLY
      });
      this._peerConnection.addTransceiver(NAME.VIDEO, {
        direction: NAME.TRANSCEIVER_DIRECTION_SENDONLY
      });
      this._log.info('initialize creating offer');

      offer = await this._peerConnection.createOffer();
      this.clientAbility = await getClientAbility(offer.sdp as string, this._room.scheduleResult.config?.remove264FromSDP || false, this._enableDataChannel, this._enableDtx);
      this.originOffer = offer;
      if (this.dtlsTransport) {
        this.dtlsTransport.onstatechange = () => {
          const { dtlsTransport } = this;
          if (!dtlsTransport) return;
          this._log.debug(`dtls state: ${dtlsTransport.state}`);
          if (dtlsTransport.state === 'connecting' && this.stat.dtlsStartTime === 0) {
            this.stat.dtlsStartTime = Date.now();
          } else if (dtlsTransport.state === 'connected' && this.stat.dtlsEndTime === 0) {
            this.stat.dtlsEndTime = Date.now();
          }
        };
      }

      kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_NETWORK.SPC_INITIALIZED });
      return this.clientAbility;
    } catch (error: any) {
      kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.SPC_INITIALIZED, error });
      this._log.error(`initialize failed ${error} \noffer: ${offer?.sdp}`);
      throw error;
    }
  }

  async setIceServers(iceServers: RTCIceServer[]) {
    if (!this._peerConnection || iceServers.length === 0) return;
    try {
      this._log.info('setIceServers', JSON.stringify(iceServers, (key, value) => ((key === 'username' || key === 'credential') ? 'hided' : value)));
      this._peerConnection.setConfiguration(this.getPeerConnectionConfig(iceServers));
      // setLocalDescription 需要在 setConfiguration 后调用，否则新配置不生效
      if (this._peerConnection?.localDescription || !this.originOffer) {
        this._log.warn('setIceServers already has localDescription or no origin Offer');
        return;
      }
      await this.setOffer(this.originOffer);
    } catch (error: any) {
      this._log.warn('setIceServers error ', error);
    }
  }

  setPriority(priority: RTCPriorityType = 'high') {
    if (!this._peerConnection) return;
    try {
      const senders = this._peerConnection.getSenders();
      senders.forEach(sender => {
        const parameters = sender.getParameters();
        if (parameters.encodings[0]) {
          parameters.encodings[0].priority = priority;
          parameters.encodings[0].networkPriority = priority;
          sender.setParameters(parameters).catch(error => {
            this._log.warn('setPriority error ', error);
          });
        }
      });
    } catch (error) {
      this._log.warn('setPriority error ', error);
    }
  }

  async connect(serverAbility: ServerAbility, callByReconnect = false) {
    try {
      if (this.currentState === ConnectionState.CONNECTED) {
        return;
      }
      if (!this._peerConnection?.localDescription && this.originOffer) {
        await this.setOffer(this.originOffer);
      }
      const prevTime = performanceNow();

      // 获取 profileLevelId 配置（优先级：URL > 云控），在生成 answer SDP 时直接注入
      const profileLevelIdConfig = this.getProfileLevelIdConfig();

      const answerSDP = getAnswerSDPFromServerAbility({
        serverAbility,
        clientAbility: this.clientAbility,
        offerSDP: this._peerConnection!.localDescription!.sdp,
        enableCustomMessage: this._enableDataChannel,
        profileLevelIdConfig
      });

      const answer = { type: 'answer', sdp: answerSDP } as RTCSessionDescriptionInit;
      this._serverAbility = serverAbility;
      await this.setAnswer(answer);
      await this.waitForPeerConnectionConnected();
      this._room.firewallDetector.resetTimeoutCount();
      const priority = this._room.scheduleResult.config?.priority || this._room.joinParams?.priority || new URLSearchParams(location.search).get('priority');
      if (priority) this.setPriority(priority as RTCPriorityType);
      if (!callByReconnect) {
        kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_NETWORK.SPC_CONNECT, cost: performanceNow() - prevTime });
      }
    } catch (error: any) {
      const isAbortedError = error instanceof RtcError && error.code === ErrorCode.API_CALL_ABORTED;
      if (!isAbortedError) {
        this._log.error(`connect failed: ${error}`, serverAbility);
      }
      this.reset();
      // 连接失败时开始重连
      if (!isAbortedError && !this.isReconnecting && !this.isDestroyed) {
        kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.SPC_CONNECT, error });
        this.emitConnectionStateChangedEvent(ConnectionState.DISCONNECTED);
        this.startReconnection();
      }
      throw error;
    }
  }

  /**
   * @private
   */
  async reconnect() {
    if (this._reconnectionTimer !== -1) {
      this._log.warn('reconnect() is reconnecting, ignore current reconnection');
      return;
    }

    // WS 未连接，等到 WS 连接上再重连
    if (!this.signalChannelInstance.isConnected) {
      this._log.warn('reconnect() wait signal channel is connected');
      this.signalChannelInstance.once(ChannelEvent.CONNECTED, this.reconnect, this);
      return;
    }

    try {
      this.reconnectionCount += 1;
      this._log.warn(`reconnect() trying [${this.reconnectionCount}]`);
      this.reset();
      const bakIp = this.signalChannelInstance.getBackupRelayIpPair();
      const clientAbility = await this.initialize(this._room.getIceServers(bakIp?.iceServer ? [bakIp.iceServer] : []));
      const data = {
        ability: clientAbility,
        ...bakIp
      };
      const result = await this.signalChannelInstance.sendWaitForResponse({
        command: SignalCommand.REBUILD_PEER_CONNECTION,
        responseCommand: SignalEvent.REBUILD_PEER_CONNECTION_RES,
        data,
        enableLog: false
      });
      if (result.data.code !== 0) {
        throw new RtcError({ code: result.data.code, message: result.data.message });
      }
      const { ability } = result.data.data;
      if (this._room.joinParams) {
        ability.useVp8 = ability.useVp8 || !!this._room.joinParams?.useVp8;
        ability.useH265 = ability.useH265 && !!this._room.joinParams?.useH265;
      }
      await this.connect(ability, true);
      kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_NETWORK.SPC_RECONNECT });
      this._log.warn('reconnect() success');
      this.stopReconnection();
      innerEmitter.emit(INNER_EVENT.SPC_RECONNECTED, { room: this._room });
      this.emit(SPC_EVENT.RECONNECTED);
    } catch (error: any) {
      // 已停止重连的情况下忽略不处理，例如重进房场景中，会重新创建 spc 连接，旧的 spc 的重连可能会超时失败，此时忽略。
      if (!this.isReconnecting || this.isDestroyed) return;
      if (error?.message.includes('timeout')) {
        const RECONNECTION_TIMEOUT = getReconnectionTimeout(this.reconnectionCount);
        this._log.warn(`reconnect() timeout, try again after ${RECONNECTION_TIMEOUT / 1000}s`);
        await delay(RECONNECTION_TIMEOUT, (timeoutId: number) => {
          this._reconnectionTimer = timeoutId;
        });
        this.clearReconnectionTimer();
        await this.reconnect();
      } else {
        this._log.error(`reconnect() failed ${error?.code} ${error}`);
        kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_NETWORK.SPC_RECONNECT, error });
        if (this.reconnectionCount >= getRetryCount()) {
          this._log.warn(`SDK has tried reconnect for ${getRetryCount()} times, but all failed, please check your network`);
        }
        this.stopReconnection();
        this.emitConnectionStateChangedEvent(ConnectionState.DISCONNECTED);
        this.emit(SPC_EVENT.ERROR);
      }
    }
  }

  getPeerConnection() {
    return this._peerConnection;
  }

  @clearCallQueue('reconnect')
  async startReconnection() {
    if (this.isReconnecting) return;
    this._log.warn('start reconnect');
    // 设为 null 避免阻塞后续更新 sdp 流程
    this._updateSDPPromise = null;
    this.emitConnectionStateChangedEvent(ConnectionState.RECONNECTING);

    await this.reconnect();

    // this.addEventInternal(
    //   this._isUplink
    //     ? EventId.TRANSPORT_UPLINK_RECONNECTING
    //     : EventId.TRANSPORT_DOWNLINK_RECONNECTING,
    //   `${this._isUplink ? 'uplink' : 'downlink'}-connection is reconnecting`
    // );
  }

  stopReconnection() {
    if (this.isReconnecting) {
      this._log.info('stop reconnect');
      this.reconnectionCount = 0;
      this.clearReconnectionTimer();
      this.clearConnectTimeout?.();
      this.signalChannelInstance.off(ChannelEvent.CONNECTED, this.reconnect, this);
      if (this.currentState === ConnectionState.RECONNECTING) {
        this.emitConnectionStateChangedEvent(ConnectionState.DISCONNECTED);
      }
    }
  }

  /**
   * 笔记本合盖后恢复 PeerConnection 重连
   * 调用时机：Websocket rebuild session 成功、重进房成功
   * ref: http://tapd.oa.com/20396022/prong/stories/view/1020396022868179443
   * @memberof SinglePeerConnection
   */
  checkPeerConnectionToReconnect() {
    if (
      !this.isReconnecting &&
      this._peerConnection?.connectionState === PEER_CONNECTION_STATE.CLOSED
    ) {
      this.startReconnection();
    }
  }

  clearReconnectionTimer() {
    if (this._reconnectionTimer !== -1) {
      clearTimeout(this._reconnectionTimer);
      this._reconnectionTimer = -1;
    }
  }

  onConnectionStateChange(event: any) {
    const ICEState = this._peerConnection?.iceConnectionState || 'closed';
    const DTLSState = this.getDTLSTransportState();
    this._log.info(`connectionState: ${event.target.connectionState} ICE: ${ICEState} DTLS: ${DTLSState}`);

    if (event.target.connectionState === PEER_CONNECTION_STATE.CONNECTING) {
      if (this.stat.peerConnectionStartTime === 0) {
        this.stat.peerConnectionStartTime = Date.now();
      }
      this.emitConnectionStateChangedEvent(ConnectionState.CONNECTING);
    }

    // |closed| should only be fired by RTCPeerConnection.close(). In case, |closed| is fired
    // by browser internal error, we reset onconnectionstatechange callback before RTCPeerConnection.close
    // and then take both |failed| and |closed| as error
    if (
      event.target.connectionState === PEER_CONNECTION_STATE.FAILED ||
      event.target.connectionState === PEER_CONNECTION_STATE.CLOSED
    ) {
      this.emitConnectionStateChangedEvent(ConnectionState.DISCONNECTED);
      if (this._room.forceRelay) {
        this.switchRelay(false);
      } else {
        this.startReconnection();
      }
    }

    if (
      event.target.connectionState === PEER_CONNECTION_STATE.CONNECTED ||
      event.target.connectionState === PEER_CONNECTION_STATE.COMPLETED
    ) {
      if (this.stat.peerConnectionEndTime === 0) {
        this.stat.peerConnectionEndTime = Date.now();
      }
      innerEmitter.emit(INNER_EVENT.SINGLE_CONNECTION_STAT, {
        room: this._room, stat: {
          ice: this.stat.iceEndTime - this.stat.iceStartTime,
          dtls: this.stat.dtlsEndTime - this.stat.dtlsStartTime,
          peerConnection: this.stat.peerConnectionEndTime - this.stat.peerConnectionStartTime
        }
      });
      this.logSelectedCandidate();
      // eventLogger.logSuccessEvent({
      //   userId: this._userId,
      //   eventType: KIBANA_EVENT.ICE_CONNECTION_STATE
      // });

      this.emitConnectionStateChangedEvent(ConnectionState.CONNECTED);
    }
  }

  getDTLSTransportState() {
    if (!this._peerConnection) {
      return DTLS_STATE_UNKNOWN;
    }

    let transport = null;
    if (!isGetSendersSupported() || this._peerConnection.getSenders().length === 0) {
      return DTLS_STATE_UNKNOWN;
    }
    // senders 共用一个 transport 实例，取第一个即可
    transport = this._peerConnection.getSenders()[0].transport;
    if (!isGetReceiversSupported() || this._peerConnection.getReceivers().length === 0) {
      return DTLS_STATE_UNKNOWN;
    }
    if (!transport) {
      return DTLS_STATE_UNKNOWN;
    }

    return transport.state;
  }

  emitConnectionStateChangedEvent(state: ConnectionState) {
    // 状态未变更，不抛事件
    if (state === this.currentState) {
      return;
    }

    // 重连过程中，不抛 Connecting 事件
    if (
      this.currentState === ConnectionState.RECONNECTING &&
      state === ConnectionState.CONNECTING
    ) {
      return;
    }

    // innerEmitter.emit(INNER_EVENT.CONNECTION_STATE_CHANGED, {
    //   room: this._room,
    //   singlePeerConnection: this,
    //   prevState: this.currentState,
    //   state
    // });

    this.emit(SPC_EVENT.CONNECTION_STATE_CHANGED, {
      prevState: this.currentState,
      state
    });
    this.currentState = state;
  }
  /**
   * 打印 selected candidate 相关信息
   * 使用 PeerConnection.getStats 接口获取 selected candidate。
   */
  async logSelectedCandidate() {
    if (!this._peerConnection) {
      return;
    }

    const stats = await this._peerConnection.getStats();
    // @ts-ignore
    for (const [key, stat] of stats) {
      if (isSelectedCandidatePair(stat)) {
        // @ts-ignore
        const local = stats.get(stat.localCandidateId);
        // @ts-ignore
        const remote = stats.get(stat.remoteCandidateId);

        // firefox 及 safari 中的 candidate 地址字段通过 address 获取。
        // chrome 的 candidate 地址字段通过 ip 获取。
        // safari 浏览器拿不到 local candidate 的 ip 地址
        if (local) {
          this._log.info(`local candidate: ${local.candidateType} ${local.protocol}:${local.ip || local.address}:${local.port} ${local.networkType || ''} ${local.relayProtocol ? `relayProtocol:${local.relayProtocol} url: ${local.url}` : ''}`);
          if (local.networkType) {
            setNetworkTypeFromWebRTC(local.networkType);
          }
        }
        if (remote) {
          this._log.info(`remote candidate: ${remote.candidateType} ${remote.protocol}:${remote.ip ||
            remote.address}:${remote.port}`);
        }
        break;
      }
    }
  }

  /**
   * 等待 PeerConnection 连接成功
   * @return {Promise}
   * @memberof Connection
   */
  waitForPeerConnectionConnected() {
    // 如果多次调用该接口，则直接返回之前的 promise 实例
    if (this._waitForPCConnectedPromise) {
      return this._waitForPCConnectedPromise;
    }
    this._waitForPCConnectedPromise = new Promise((resolve, reject) => {
      if (this.currentState === ConnectionState.CONNECTED) {
        return resolve();
      }
      const onConnectionStateChange = (event: any) => {
        if (event.state !== ConnectionState.CONNECTED) {
          return;
        }
        clearTimeout(timeoutId);
        uninstallEvents();
        resolve();
      };

      // 在 publish/subscribe pending 时，业务侧调用 leave 接口，可能导致 connection 连接失败
      // 此处细分该场景的错误。
      const onLeave = ({ room }: { room: IRoom }) => {
        if (room !== this._room) return;
        clearTimeout(timeoutId);
        uninstallEvents();
        reject(new RtcError({
          code: ErrorCode.API_CALL_ABORTED,
          message: generateErrorMessage({
            key: ErrorName.CONNECTION_ABORTED,
            data: 'leave room'
          })
        }));
      };

      const uninstallEvents = () => {
        innerEmitter.off(INNER_EVENT.LEAVE_SUCCESS, onLeave, this);
        this.off(SPC_EVENT.CONNECTION_STATE_CHANGED, onConnectionStateChange, this);
      };

      const timeoutId = setTimeout(() => {
        uninstallEvents();
        const error = new RtcError({
          code: ErrorCode.API_CALL_TIMEOUT,
          message: 'connection timeout'
        });
        this._room.firewallDetector.increaseTimeoutCount();
        reject(error);
      }, PEERCONNECTION_CONNECTING_TIMEOUT);
      this.clearConnectTimeout = () => {
        uninstallEvents();
        clearTimeout(timeoutId);
        delete this.clearConnectTimeout;
      };
      this.clearWaitForConnectedPromise = () => {
        this._waitForPCConnectedPromise = null;
        reject(new RtcError({
          code: ErrorCode.API_CALL_TIMEOUT,
          message: 'connection timeout'
        }));
      };
      innerEmitter.on(INNER_EVENT.LEAVE_SUCCESS, onLeave, this);
      this.on(SPC_EVENT.CONNECTION_STATE_CHANGED, onConnectionStateChange, this);
    });
    // resolved/rejected 时，清理 promise
    this._waitForPCConnectedPromise = this._waitForPCConnectedPromise
      .finally(() => {
        this._waitForPCConnectedPromise = null;
        delete this.clearConnectTimeout;
      });
    return this._waitForPCConnectedPromise;
  }

  waitForReconnected() {
    if (!this.isReconnecting) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      this.once(SPC_EVENT.RECONNECTED, resolve);
      this.once(SPC_EVENT.ERROR, reject);
    });
  }

  @addCallQueueValidArgs((options: any) => options.userId)
  async addDownlink(options: { userId: string; tinyId: string; ssrc: SSRC; prevMids?: number[] }) {
    this._log.info(`addDownlink(${options.userId}) trying`);
    if (this.isReconnecting) {
      await this.waitForReconnected();
    }
    if (this._updateSDPPromise) await this._updateSDPPromise;

    this.updateLocalAndRemoteSDPConfig(options);
    if (this.addDownlinkQueue.size === 0) {
      try {
        await this.updateSDP();
        this._log.info(`addDownlink(${options.userId}) done`);
      } catch (error) {
        this._log.error(`addDownlink(${options.userId}) failed ${error}`);
        // // await this.removeDownlink(options.tinyId, options.userId);
        await this.startReconnection();
      }
    }
  }

  updateLocalAndRemoteSDPConfig({ ssrc, userId, tinyId, prevMids }: { ssrc: SSRC; userId: string, tinyId: string, prevMids?: number[] }) {
    if (!this._peerConnection || !this.parsedAnswer) return;
    this._log.info(`updateLocalAndRemoteSDPConfig ${userId} ${JSON.stringify(ssrc)}`);
    const existedList = this.parsedAnswer.media.filter(item => item.ssrcs?.find(ssrc => ssrc.value?.includes(tinyId)));
    let audio; let bigVideo; let auxVideo;

    if (existedList.length === 3) {
      audio = existedList[0];
      bigVideo = existedList[1];
      auxVideo = existedList[2];
    } else {
      // 优先复用传入的 prevMids（如果处于 inactive 状态），避免 mid 变化导致 track 发生变化
      const transceivers = this._peerConnection.getTransceivers().slice(4);
      const prevMidsAreInactive = prevMids?.length === 3 && prevMids.every(mid => transceivers.find(t => Number(t.mid) === mid)?.direction === 'inactive');

      let inactiveMidList: number[];
      if (prevMidsAreInactive) {
        // 复用之前的 mid
        inactiveMidList = prevMids;
        this._log.info(`reusing previous mids for ${userId}: ${inactiveMidList.join(',')}`);
        transceivers.forEach(t => {
          if (inactiveMidList.includes(Number(t.mid))) {
            t.direction = NAME.TRANSCEIVER_DIRECTION_RECVONLY;
          }
        });
      } else {
        // 寻找任意 3 个 inactive 的 mid
        inactiveMidList = transceivers
          .filter(item => item.direction === 'inactive')
          .slice(0, 3)
          .map(item => {
            item.direction = NAME.TRANSCEIVER_DIRECTION_RECVONLY;
            return Number(item.mid);
          });
      }

      if (inactiveMidList.length === 3) {
        audio = this.parsedAnswer!.media.find(item => Number(item.mid) === Number(inactiveMidList[0]));
        bigVideo = this.parsedAnswer!.media.find(item => Number(item.mid) === Number(inactiveMidList[1]));
        auxVideo = this.parsedAnswer!.media.find(item => Number(item.mid) === Number(inactiveMidList[2]));
      } else if (inactiveMidList.length === 0) {
        this._peerConnection.addTransceiver(NAME.AUDIO, {
          direction: NAME.TRANSCEIVER_DIRECTION_RECVONLY
        });
        this._peerConnection.addTransceiver(NAME.VIDEO, {
          direction: NAME.TRANSCEIVER_DIRECTION_RECVONLY
        });
        this._peerConnection.addTransceiver(NAME.VIDEO, {
          direction: NAME.TRANSCEIVER_DIRECTION_RECVONLY
        });
        audio = JSON.parse(JSON.stringify(this.parsedAnswer!.media[0]));
        const videoMediaConfig = getVideoMediaConfig({
          mid: 1,
          serverAbility: this._serverAbility!,
          clientAbility: this.clientAbility!,
          parsedOffer: parse(this._peerConnection.localDescription!.sdp),
          isDownlink: true // 下行 answer video 的 codec 描述，使用后台给的所有 ability
          // 上行的 codec 描述，根据后台 ability useVp8 参数来选择 h264 or vp8,
        });
        bigVideo = JSON.parse(JSON.stringify(videoMediaConfig));
        auxVideo = JSON.parse(JSON.stringify(videoMediaConfig));

        audio.mid = this.parsedAnswer!.media.length;
        this.parsedAnswer!.media.push(audio);

        bigVideo.mid = this.parsedAnswer!.media.length;
        this.parsedAnswer!.media.push(bigVideo);

        auxVideo.mid = this.parsedAnswer!.media.length;
        this.parsedAnswer!.media.push(auxVideo);
      }
    }
    audio.direction = NAME.TRANSCEIVER_DIRECTION_SENDONLY;
    // 确保 msid 的唯一性，ssrc 是可以确保唯一性的。
    const msid = `${tinyId}-${ssrc.audio}`;
    audio.ssrcs = [
      {
        id: ssrc.audio,
        attribute: 'cname',
        value: `${msid}`
      },
      {
        id: ssrc.audio,
        attribute: 'msid',
        value: `${msid}-${NAME.MAIN} ${msid}-audio`
      }
    ];
    bigVideo.direction = NAME.TRANSCEIVER_DIRECTION_SENDONLY;
    bigVideo.ssrcs = [
      {
        id: ssrc.video,
        attribute: 'cname',
        value: `${msid}`
      },
      {
        id: ssrc.video,
        attribute: 'msid',
        value: `${msid}-${NAME.MAIN} ${msid}-bigvideo`
      },
      {
        id: ssrc.videoRtx,
        attribute: 'cname',
        value: `${msid}`
      },
      {
        id: ssrc.videoRtx,
        attribute: 'msid',
        value: `${msid}-${NAME.MAIN} ${msid}-bigvideo`
      }
    ];
    bigVideo.ssrcGroups = [
      { semantics: 'FID', ssrcs: `${ssrc.video} ${ssrc.videoRtx}` }
    ];
    auxVideo.direction = NAME.TRANSCEIVER_DIRECTION_SENDONLY;
    const auxIdentifier = `${msid}-aux`;
    auxVideo.ssrcs = [
      {
        id: ssrc.auxiliary,
        attribute: 'cname',
        value: auxIdentifier
      },
      {
        id: ssrc.auxiliary,
        attribute: 'msid',
        value: `${auxIdentifier} ${msid}-aux${NAME.VIDEO}`
      },
      {
        id: ssrc.auxiliaryRtx,
        attribute: 'cname',
        value: `${auxIdentifier} ${msid}-aux${NAME.VIDEO}`
      },
      {
        id: ssrc.auxiliaryRtx,
        attribute: 'msid',
        value: `${auxIdentifier} ${msid}-aux${NAME.VIDEO}`
      }
    ];
    auxVideo.ssrcGroups = [
      { semantics: 'FID', ssrcs: `${ssrc.auxiliary} ${ssrc.auxiliaryRtx}` }
    ];

    if (this.parsedAnswer?.groups) {
      this.parsedAnswer.groups[0].mids = this.parsedAnswer.media.map(item => item.mid).join(' ');
    }
    this._downlinkMIDMap.set(userId, [audio.mid, bigVideo.mid, auxVideo.mid]);
    this._downlinkMIDUserIDMap.set(audio.mid, userId);
    this._downlinkMIDUserIDMap.set(bigVideo.mid, userId);
    this._downlinkMIDUserIDMap.set(auxVideo.mid, userId);
  }

  @addCallQueueValidArgs(userId => userId)
  async removeDownlink(userId: string) {
    if (!this._downlinkMIDMap.has(userId) || !this._peerConnection || !this.parsedAnswer) return;
    this._log.info(`removeDownlink(${userId}) trying`);

    if (this.isReconnecting) {
      await this.waitForReconnected();
    }
    if (this._updateSDPPromise) await this._updateSDPPromise;
    const mids = this._downlinkMIDMap.get(userId);
    let isNeedToUpdateSDP = false;
    this._peerConnection.getTransceivers().forEach(transceiver => {
      if (mids?.includes(Number(transceiver.mid))) {
        isNeedToUpdateSDP = true;
        transceiver.direction = 'inactive';
      }
    });

    this.parsedAnswer.media.forEach(item => {
      if (mids?.includes(Number(item.mid))) {
        isNeedToUpdateSDP = true;
        item.direction = 'inactive';
        // removeDownlink 将对应的 ssrc 清除，避免后续 addDownlink 出现相同的 ssrc，出现冲突导致更新 sdp 失败。
        item.ssrcs = [];
        item.ssrcGroups = [];
      }
    });

    if (this.removeDownlinkQueue.size === 0 && isNeedToUpdateSDP) {
      await this.updateSDP();
    }
    this._downlinkMIDMap.delete(userId);
    mids?.forEach(mid => this._downlinkMIDUserIDMap.delete(mid));
    this._log.info(`removeDownlink(${userId}) done`);
    return mids;
  }

  /**
   * 设置上行音视频码率，策略如下：
   * 1. 优先使用 set maxBitrate 方式控制码率；
   * 优点：各浏览器均支持，不需要修改 SDP
   * 缺点：需要考虑低版本兼容性
   * 2. 若 set maxBitrate 不支持或者失败了，则使用
   * 修改 SDP 方式控制码率，作为备选方案。
   *
   * ref: https://developer.mozilla.org/en-US/docs/Web/API/RTCRtpEncodingParameters/maxBitrate
   * @param {Object} options
   * @param {number} [options.audio]
   * @param {number} [options.bigVideo]
   * @param {number} [options.smallVideo]
   * @param {number} [options.auxVideo]
   */
  async setBandwidth(options: { audio?: number, bigVideo?: number, smallVideo?: number, auxVideo?: number }) {
    if (!this._peerConnection) return;
    const { audio, bigVideo, smallVideo, auxVideo } = options;
    try {
      if (isSetParametersSupported()) {
        const senders = this._peerConnection.getSenders().slice(0, 4);
        for (let i = 0; i < senders.length; i++) {
          const sender = senders[i];
          let bandwidth;
          if (i === 0 && audio) {
            bandwidth = audio;
          } else if (i === 1 && bigVideo) {
            bandwidth = bigVideo;
          } else if (i === 2 && smallVideo) {
            bandwidth = smallVideo;
          } else if (i === 3 && auxVideo) {
            bandwidth = auxVideo;
          }
          if (!bandwidth) continue;
          await this.setSenderMaxBitrate(sender, bandwidth);
        }
        // setStartBitrate 只在首次设置时追加 x-google-start-bitrate 并触发 updateSDP。
        // 推流中（已有该字段）不再追加，也不触发 updateSDP，避免全局 SDP 协商导致其他流掉帧。
        let isNeedToUpdateSDP = false;
        if (bigVideo) {
          isNeedToUpdateSDP = this.setStartBitrate(1, bigVideo);
        }
        if (auxVideo) {
          isNeedToUpdateSDP = this.setStartBitrate(3, auxVideo) || isNeedToUpdateSDP;
        }
        if (isNeedToUpdateSDP) {
          await this.updateSDP();
        }
      } else {
        await this.setBandwidthBySDP(options);
      }

      this._log.info(`setBandwidth ${JSON.stringify(options)}`);
    } catch (error) {
      this._log.error(`failed to set bandwidth: ${error}`);
    }
  }

  setStartBitrate(index: number, startBitrate: number) {
    const fmtp = this.parsedAnswer?.media[index]?.fmtp[0];
    if (!this._peerConnection?.remoteDescription || !fmtp) return false;
    // 只在首次设置时生效，推流中 start-bitrate 不再生效，
    // 且 updateSDP 会触发全局 SDP 重新协商，导致其他流（如屏幕分享）掉帧。
    if (fmtp.config.includes('x-google-start-bitrate')) return false;
    // 起始码率最大值 5000 kbps，避免客户设置很大的值，快速占满带宽。
    fmtp.config += `;x-google-start-bitrate=${Math.min(startBitrate, 5000)}`;
    return true;
  }

  clearStartBitrate(index: number) {
    const fmtp = this.parsedAnswer?.media[index]?.fmtp[0];
    if (!fmtp) return;
    fmtp.config = fmtp.config.replace(/;x-google-start-bitrate=\d+/, '');
  }

  setSenderMaxBitrate(sender: RTCRtpSender, bandwidth: number | 'unlimited') {
    const parameters = sender.getParameters();
    if (!parameters.encodings || parameters.encodings.length === 0) {
      parameters.encodings = [{}];
    }
    if (bandwidth === 'unlimited') {
      delete parameters.encodings[0].maxBitrate;
    } else {
      if (parameters.encodings[0].maxBitrate === bandwidth * 1000) return;
      parameters.encodings[0].maxBitrate = bandwidth * 1000;
    }
    return sender.setParameters(parameters);
  }

  setBandwidthBySDP({ audio, bigVideo, smallVideo, auxVideo }: { audio?: number, bigVideo?: number, smallVideo?: number, auxVideo?: number }) {
    if (!this._peerConnection || !this._peerConnection.localDescription || !this.parsedAnswer) return;
    const parsedOfferSDP = parse(this._peerConnection.localDescription.sdp);

    const type = IS_FIREFOX ? 'TIAS' : 'AS';
    if (audio) {
      parsedOfferSDP.media[0].bandwidth = [{ type, limit: IS_FIREFOX ? audio * 1000 : audio }];
      this.parsedAnswer.media[0].bandwidth = [{ type, limit: IS_FIREFOX ? audio * 1000 : audio }];
    }
    if (bigVideo) {
      parsedOfferSDP.media[1].bandwidth = [
        { type, limit: IS_FIREFOX ? bigVideo * 1000 : bigVideo }
      ];
      this.parsedAnswer.media[1].bandwidth = [
        { type, limit: IS_FIREFOX ? bigVideo * 1000 : bigVideo }
      ];
    }
    if (smallVideo) {
      parsedOfferSDP.media[2].bandwidth = [
        { type, limit: IS_FIREFOX ? smallVideo * 1000 : smallVideo }
      ];
      this.parsedAnswer.media[2].bandwidth = [
        { type, limit: IS_FIREFOX ? smallVideo * 1000 : smallVideo }
      ];
    }
    if (auxVideo) {
      parsedOfferSDP.media[3].bandwidth = [
        { type, limit: IS_FIREFOX ? auxVideo * 1000 : auxVideo }
      ];
      this.parsedAnswer.media[3].bandwidth = [
        { type, limit: IS_FIREFOX ? auxVideo * 1000 : auxVideo }
      ];
    }

    const localDescription = { type: 'offer', sdp: write(parsedOfferSDP) } as RTCSessionDescriptionInit;
    return this.updateSDP({ localDescription });
  }

  setScaleResolutionDownBy(sender: RTCRtpSender, scaleResolutionDownBy: number, streamType: RemoteStreamType) {
    const parameters = sender.getParameters();
    if (!parameters.encodings || parameters.encodings.length === 0) {
      parameters.encodings = [{}];
    }
    const prevScaleResolutionDownBy = parameters.encodings[0].scaleResolutionDownBy;
    // 第一次设置，scale = 1 则忽略
    // 非第一次设置，prevScale === scale ，则忽略
    if (isUndefined(prevScaleResolutionDownBy) ? scaleResolutionDownBy === 1 : scaleResolutionDownBy === prevScaleResolutionDownBy) return;
    let log = `setScaleResolutionDownBy ${streamType} ${scaleResolutionDownBy}`;
    if (prevScaleResolutionDownBy) log += ` prevScale: ${prevScaleResolutionDownBy}`;
    this._log.warn(log);
    parameters.encodings[0].scaleResolutionDownBy = scaleResolutionDownBy;
    return sender.setParameters(parameters);
  }

  setDegradationPreference(sender: RTCRtpSender, contentHint: VideoContentHint, streamType: RemoteStreamType) {
    if ((IS_CHROMIUM_BASE && CHROME_MAJOR_VERSION < 83) || (IS_IOS && isVersionSmallerThan(IOS_VERSION, '12.1')) || (IS_FIREFOX && FIREFOX_MAJOR_VERSION < 138)) return;
    const parameters = sender.getParameters();
    let degradationPreference: RTCDegradationPreference = 'balanced';
    if (contentHint === VideoContentHint.MOTION) {
      degradationPreference = 'maintain-framerate';
    } else if (contentHint === VideoContentHint.DETAIL) {
      degradationPreference = 'maintain-resolution';
    }
    // @ts-ignore
    if (parameters.degradationPreference === degradationPreference) return;
    const log = `setDegradationPreference ${streamType} ${degradationPreference}`;
    this._log.info(log);
    // @ts-ignore
    parameters.degradationPreference = degradationPreference;
    return sender.setParameters(parameters).catch(error => this._log.warn(`${log} failed: ${error}`));
  }

  // 避免出现冲突，确保上一次执行结束后，再执行 sdp 更新。
  @addCallQueue(true)
  updateSDP({ localDescription }: {
    localDescription?: RTCSessionDescriptionInit;
  } = {}) {
    if (!this._parsedAnswer) return Promise.resolve();
    const answerSDP = write(this._parsedAnswer);
    this._updateSDPPromise = new Promise(async (resolve, reject) => {
      try {
        if (!localDescription && this._peerConnection) {
          this._log.info('creating offer');
          localDescription = await this._peerConnection.createOffer();
        }
        if (localDescription) {
          await this.setOffer(localDescription);
        }
        await this.setAnswer({ type: 'answer', sdp: answerSDP });
        this._updateSDPPromise = null;
        resolve();
      } catch (error) {
        this._log.error(error);
        // 只打印一次 sdp，因为单 pc 的 sdp 很大，往往出现一次 sdp 更新失败，后续的 sdp 都会更新失败，避免重复打印 sdp。
        // 造成不必要的内存占用和日志上报。
        if (!this._isSDPLogged && this._peerConnection) {
          this._log.warn(`current offer: ${this.filterSDPDirection(this._peerConnection.localDescription?.sdp)} \nnext offer: ${this.filterSDPDirection(localDescription?.sdp)}`);
          this._log.warn(`current answer: ${this.filterSDPDirection(this._peerConnection.remoteDescription?.sdp)} \nnext answer: ${this.filterSDPDirection(answerSDP)}`);
          this._log.warn(`offer: ${localDescription?.sdp}`);
          this._log.warn(`answer: ${answerSDP}`);
          // @ts-ignore
          this._log.warn(`transceivers: ${JSON.stringify(this._peerConnection.getTransceivers().map(({ mid, currentDirection, direction, stopped }) => ({ mid, currentDirection, direction, stopped })))}`);
          this._log.warn(`parsedAnswer: ${JSON.stringify(this._parsedAnswer)}`);
          this._isSDPLogged = true;
        }
        this._updateSDPPromise = null;
        reject(error);
      }
    });
    return this._updateSDPPromise;
  }

  /**
   * Firefox 在 sender.replaceTrack(null) 后，还会继续发最后一帧数据，
   * 导致在取消推流、removeTrack 后，远端用户还能收到数据。
   *
   * 此处针对 Firefox 做兼容：
   * 1. 在 replaceTrack(null) 后将 transceiver.direction 设为 'inactive'
   * 2. 在 replaceTrack(track) 后再将 transceiver.direction 设为 'sendonly'
   * @param {'inactive'|'sendonly'} direction
   * @param {Number[]} transceiverIndexList
   * @returns
   */
  async setTransceiverDirection(direction: RTCRtpTransceiverDirection, transceiverIndexList: number[]) {
    if (!IS_FIREFOX || !this._peerConnection || !this._parsedAnswer) return;
    this._log.info(`setting transceiver ${transceiverIndexList.join(',')} direction to ${direction}`);
    const transceivers = this._peerConnection.getTransceivers();
    transceiverIndexList.forEach(transceiverIndex => {
      if (transceivers[transceiverIndex].direction !== direction) {
        transceivers[transceiverIndex].direction = direction;
      }
    });
    for (const index of transceiverIndexList) {
      const answerDirection = this._parsedAnswer.media[index].direction;
      // 将 answer 中的 recvonly 改为 inactive
      if (direction === TRANSPORT_DIRECTION.INACTIVE && answerDirection === TRANSPORT_DIRECTION.RECVONLY) {
        this._parsedAnswer.media[index].direction = direction as SharedAttributes['direction'];
      }
      // 将 answer 中的 inactive 改为 recvonly
      if (direction === TRANSPORT_DIRECTION.SENDONLY && answerDirection === TRANSPORT_DIRECTION.INACTIVE) {
        // 对于 offer 来说，direction 是 sendonly
        // 对于 answer 来说，direction 是 recvonly
        this._parsedAnswer.media[index].direction = TRANSPORT_DIRECTION.RECVONLY as SharedAttributes['direction'];
      }
    }
    await this.updateSDP();
  }

  filterSDPDirection(sdp = '') {
    const parsedSDP = parse(sdp);
    return parsedSDP.media.map(item => item.direction);
  }

  @reportSuccessRate(KV_REPORT_KEY_NETWORK.SET_LOCAL_DESCRIPTION, false)
  @checkPendingPromise(10, CheckPendingPromiseType.PENDING) // 稳妥起见超时先保持 pending 状态，不影响原有逻辑，等版本发布后观察下线上 case 数量再做下一步打算
  setOffer(offer: RTCSessionDescriptionInit) {
    this._log.info('setting offer');
    const sdp = handleOfferSDP(offer.sdp!, this.clientAbility, this._serverAbility);
    this._log.debug(sdp);
    return this._peerConnection!.setLocalDescription({ type: 'offer', sdp });
  }

  @reportSuccessRate(KV_REPORT_KEY_NETWORK.SET_REMOTE_DESCRIPTION, false)
  @checkPendingPromise(10, CheckPendingPromiseType.PENDING)
  setAnswer(answer: RTCSessionDescriptionInit) {
    this._log.info('setting answer');
    this._log.debug(answer.sdp);
    return this._peerConnection!.setRemoteDescription(answer);
  }

  /**
   * 切换视频编码器。
   *
   * 视频编码器选择逻辑：https://iwiki.woa.com/p/4014069903
   */
  async switchVideoEncoder(encoder: VideoCodec) {
    if (!this._peerConnection || !this.parsedAnswer || !this._serverAbility) return;
    let isNeedToUpdateSDP = false;
    this.parsedAnswer.media.forEach(item => {
      if (item.type === NAME.VIDEO) {
        const newCodecConfig = this._serverAbility!.video.codecs.find(item => item.codec.toLowerCase() === (encoder));
        if (newCodecConfig && !item.payloads?.includes(String(newCodecConfig.payload))) {
          item.fmtp = [];
          item.payloads = '';
          item.rtp = [];
          item.rtcpFb = [];
          initCodecConfig(item, newCodecConfig);
          isNeedToUpdateSDP = true;
        }
      }
    });
    if (isNeedToUpdateSDP) {
      this._log.warn(`switch video encoder to ${encoder}`);
      await this.updateSDP();
    }
  }

  /**
   * 获取调度配置的 Profile Level ID
   * @param streamType - 流类型（大流/小流/辅流）
   * @returns Profile Level ID
   */
  getScheduleProfileLevelId(streamType?: MULTI_VIDEO_DATA_TYPE): string {
    try {
      const cfg = this._room.scheduleResult.config?.profileLevelId;
      let raw = '';
      if (streamType === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG) {
        raw = isString(cfg?.big) ? cfg.big : '';
      } else if (streamType === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_SMALL_IMG) {
        raw = isString(cfg?.small) ? cfg.small : '';
      } else if (streamType === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_AUX_IMG) {
        raw = isString(cfg?.aux) ? cfg.aux : '';
      }
      if (!raw) return '';

      const resolved = convertProfileLevelIdToHex(raw, this.clientAbility);
      if (resolved) {
        this._log.info(`use schedule profile level id: streamType=${streamType}, raw=${raw}, resolved=${resolved}`);
      } else {
        this._log.warn(`schedule profile level id not resolved: streamType=${streamType}, raw=${raw}`);
      }
      return resolved;
    } catch (error) {
      this._log.warn(`getScheduleProfileLevelId error: ${error}`);
      return '';
    }
  }

  /**
   * 获取 profileLevelId 配置
   * 优先级：URL 参数 > 云控配置
   * 注意：high profile 只对分辨率 >= 720p 的视频生效
   * @returns 如果 URL 参数存在，则 big/small/aux 都使用同一个值；否则使用云控的各自配置；都没有则返回 undefined
   */
  getProfileLevelIdConfig(): { big?: string | string[]; small?: string | string[]; aux?: string | string[] } | undefined {
    try {
      const urlProfileRaw = new URLSearchParams(location.search).get('profileLevelId') || '';
      const urlProfile = convertProfileLevelIdToHex(urlProfileRaw, this.clientAbility);

      if (urlProfile) {
        // URL 参数优先级最高，作用于所有 stream
        this._log.info(`use url profile level id: raw=${urlProfileRaw}, resolved=${urlProfile}`);
        return { big: urlProfile, small: urlProfile, aux: urlProfile };
      }

      const big = this.getScheduleProfileLevelId(MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG);
      const small = this.getScheduleProfileLevelId(MULTI_VIDEO_DATA_TYPE.MULTI_DATA_SMALL_IMG);
      const aux = this.getScheduleProfileLevelId(MULTI_VIDEO_DATA_TYPE.MULTI_DATA_AUX_IMG);

      if (big || small || aux) {
        const result: { big?: string; small?: string; aux?: string } = {};
        if (big) result.big = big;
        if (small) result.small = small;
        if (aux) result.aux = aux;
        return result;
      }

      // iOS 26.2+ 使用多 profile-level-id 规避视频编码失败问题
      if (IS_IOS && (isVersionLargerThan(IOS_VERSION, '26.2', true) || isVersionLargerThan(SAFARI_VERSION, '26.2', true) || (IS_WECHAT && isVersionLargerThan(IOS_VERSION, '18.7', true)))) {
        const profiles: string[] = [];
        const highProfile = convertProfileLevelIdToHex('high', this.clientAbility);
        const baselineProfile = convertProfileLevelIdToHex('baseline', this.clientAbility);
        if (highProfile) profiles.push(highProfile);
        if (baselineProfile) profiles.push(baselineProfile);
        if (profiles.length > 0) {
          this._log.warn(`use multi profile level ids: ${profiles.join(', ')} for fallback 26.2 video encode issue`);
          return { big: profiles, small: profiles, aux: profiles };
        }
      }
    } catch (error) {
      this._log.warn(`getProfileLevelIdConfig error: ${error}`);
      return;
    }
  }

  /**
   * 设置指定流的 H264 profile-level-id
   * @param streamType 流类型
   * @param preferHighProfile 是否偏好 High Profile，false 时使用 serverAbility 返回的第一个非 high profile
   */
  async setH264ProfileLevelId(streamType: RemoteStreamType, preferHighProfile: boolean) {
    if (!this._peerConnection || !this._serverAbility) return;
    if (this._updateSDPPromise) await this._updateSDPPromise;

    this._log.info(`set H264 profile-level-id to ${preferHighProfile ? 'high' : 'default'} for ${streamType}`);

    const mediaIndex = streamType === RemoteStreamType.Main ? 1 : 3; // mid 1=big, 3=aux（固定三路上行视频描述）
    const mediaItem = this.parsedAnswer?.media[mediaIndex];
    if (!mediaItem || mediaItem.type !== NAME.VIDEO) return;

    const rtpList = (mediaItem.rtp || []) as { payload: number; codec: string; rate: number }[];
    const fmtpList = (mediaItem.fmtp || []) as { payload: number; config: string }[];
    const h264Rtp = rtpList.find(r => r.codec?.toLowerCase() === 'h264');
    if (!h264Rtp) return;
    const fmtpItem = fmtpList.find(f => String(f.payload) === String(h264Rtp.payload));
    if (!fmtpItem) return;

    const currentProfileLevelId = extractProfileLevelIdFromFmtp(fmtpItem.config);
    if (!currentProfileLevelId) return;

    const currentProfile = getH264ProfileByProfileLevelId(currentProfileLevelId);
    const isCurrentHigh = currentProfile === 'high';

    // 当前状态与目标一致，无需修改
    if (preferHighProfile && isCurrentHigh) return;
    if (!preferHighProfile && !isCurrentHigh) return;

    // 从服务端返回的能力里找目标 profileLevelId
    const ids = this._serverAbility.video.codecs
      .map(codec => extractProfileLevelIdFromFmtp(codec.fmtp))
      .filter(Boolean) as string[];

    const targetProfileLevelId = ids.find(id => {
      const profile = getH264ProfileByProfileLevelId(id);
      return preferHighProfile ? profile === 'high' : profile !== 'high';
    });
    if (!targetProfileLevelId) return;

    const prev = fmtpItem.config;
    fmtpItem.config = patchFmtpProfileLevelId(fmtpItem.config, targetProfileLevelId);
    if (fmtpItem.config === prev) return;

    await this.updateSDP();
    this._log.info(`set H264 profile-level-id to ${preferHighProfile ? 'high' : 'default'} success`);
  }

  async useHWEncoder(hwEncoder = true, streamType?: MULTI_VIDEO_DATA_TYPE) {
    if (!this._peerConnection || !this._parsedAnswer || !this._serverAbility) return;
    let isNeedToUpdateSDP = false;
    let mediaList = [];
    if (isUndefined(streamType)) {
      mediaList = this._parsedAnswer.media.slice(1, 4);
    } else if (streamType === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG) {
      mediaList.push(this._parsedAnswer.media[1]);
    } else if (streamType === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_SMALL_IMG) {
      mediaList.push(this._parsedAnswer.media[2]);
    } else if (streamType === MULTI_VIDEO_DATA_TYPE.MULTI_DATA_AUX_IMG) {
      mediaList.push(this._parsedAnswer.media[3]);
    }
    mediaList.forEach(item => {
      if (item.type === NAME.VIDEO) {
        let newCodecConfig: AbilityVideoCodec | undefined;
        if (hwEncoder && this.is42001fSupported) {
          newCodecConfig = this.clientAbility.video.codecs.find(item => item.fmtp.includes('42001f'));
        } else if (!hwEncoder) {
          newCodecConfig = this._serverAbility!.video.codecs.find(item => item.codec.toLowerCase() === (this._serverAbility!.useVp8 ? 'vp8' : 'h264'));
        }
        if (newCodecConfig && !item.payloads?.includes(String(newCodecConfig.payload))) {
          item.fmtp = [];
          item.payloads = '';
          item.rtp = [];
          item.rtcpFb = [];
          initCodecConfig(item, newCodecConfig);
          isNeedToUpdateSDP = true;
        }
      }
    });
    if (isNeedToUpdateSDP) {
      this._log.warn(`use ${hwEncoder ? 'hw' : 'sw'} encoder`);
      await this.updateSDP();
    }
  }

  sendDataChannelMessage(arraybuffer: ArrayBuffer) {
    this._datachannel?.send(arraybuffer);
  }

  reset() {
    if (this._peerConnection) {
      this._peerConnection.close();
      // webrtc-adapter 在 safari 会加事件 listener，导致内存泄漏，此时需要手动移除
      // @ts-ignore
      this._peerConnection.removeEventListener('track', this._peerConnection._onaddstreampoly, this);
      // @ts-ignore
      this._peerConnection._onaddstreampoly = null;
      this._peerConnection = null;
    }
    this._datachannel = null;
    this.clearWaitForConnectedPromise?.();
    this._parsedAnswer = null;
    this.originOffer = null;
  }

  @clearPendingPromise()
  close() {
    this._log.info('close pc');
    this.isDestroyed = true;
    this.removeRTCListener();
    this.insertableStreamsAbortMap.forEach(controller => isFunction(controller.abort) && controller.abort('destroy'));
    this.insertableStreamsAbortMap.clear();
    this.reset();
    this.emitConnectionStateChangedEvent(ConnectionState.DISCONNECTED);
    this._downlinkMIDMap.clear();
    this.stopReconnection();
    this.removeAllListeners();
    this._room.healthDetector.off(HealthDetectorEvent.BAD_HEALTH, this.onBadHealth, this);
  }
  getReceiversByUserId(userId: string) {
    if (!this._peerConnection) return [];
    const mids = this._downlinkMIDMap.get(userId) || [];
    const transceivers = this._peerConnection.getTransceivers();
    return mids.map(mid => {
      const transceiver = transceivers.find(t => Number(t.mid) === mid);
      return transceiver?.receiver;
    }).filter(Boolean) as RTCRtpReceiver[];
  }

  get isUsingRelay() {
    return this._room.getIceTransportPolicy() === 'relay';
  }

  // 动态切换 tcp 和 udp 媒体通道
  // https://tapd.woa.com/tapd_fe/10155901/story/detail/1010155901119891080
  detectTCPAndUDP({ uplinkRTT, downlinkRTT }: NetworkQuality) {
    if (this.currentState !== ConnectionState.CONNECTED || (this._isRelayTried && !this._room.forceRelay) || this._room.getIceServers().length === 0) return;

    const wsRTT = this.signalChannelInstance.rtt;
    const mediaRTT = Math.max(uplinkRTT, downlinkRTT);
    const { rttRatioThreshold, rttThreshold } = this._room.scheduleResult.config?.useTurnTcpInfo || {};

    if (!rttRatioThreshold || !rttThreshold || !wsRTT || !mediaRTT) return;

    const rttRatio = Math.floor(mediaRTT / wsRTT);
    const isRTTOverThreshold = (this._isRelayTried || rttRatio > rttRatioThreshold) && mediaRTT > rttThreshold;

    // 判断条件需要：连续多次超出阈值，如果不是连续的，则重置
    if (!isRTTOverThreshold) {
      this._rttOverCount = 0;
      return;
    }

    if (++this._rttOverCount < 5) return;
    this._log.warn(`detectTCPAndUDP ws-rtt: ${wsRTT} upRTT: ${uplinkRTT} downRTT: ${downlinkRTT} ratio: ${rttRatio} over-count: ${this._rttOverCount} isOver: ${isRTTOverThreshold} isRelayTried: ${this._isRelayTried} force-relay: ${this._room.forceRelay}`);
    if (!this.isUsingRelay && !this._isRelayTried) {
      this._isRelayTried = true;
      this._rttOverCount = 0;
      this.switchRelay(true);
    } else if (this._room.forceRelay) {
      this.switchRelay(false);
    }
  }

  async switchRelay(isRelay: boolean, isRetry = false) {
    if (this.isUsingRelay === isRelay) return;
    const type = isRelay ? 'relay' : 'udp';
    const key = isRelay ? KV_REPORT_KEY_NETWORK.SWITCH_RELAY : KV_REPORT_KEY_NETWORK.SWITCH_RELAY_FALLBACK;
    try {
      this._room.forceRelay = isRelay;
      this._log.warn(`switchRelay ${type}`);
      const startTime = Date.now();
      await this.doSwitchRelay(type);
      this._log.warn(`switchRelay ${type} success`);
      kvStatManager.addSuccessEvent({ key, cost: Date.now() - startTime });
    } catch (error: any) {
      this._log.warn(`switchRelay ${type} failed`, error);
      kvStatManager.addFailedEvent({ key, error });

      // 重试也失败了，就重进房恢复。
      if (isRetry) {
        this._room.reJoin();
      } else {
        // 切换失败，切换回去，只重试一次
        await this.switchRelay(!isRelay, true);
      }
    }
  }

  doSwitchRelay(type: 'relay' | 'udp') {
    return new Promise((resolve, reject) => {
      // switch timeout 10s
      const reconnectTimeout = setTimeout(() => {
        this.stopReconnection();
        reject(new Error(`switch ${type} timeout`));
      }, 10000);
      this.startReconnection().then(resolve, reject)
        .finally(() => clearTimeout(reconnectTimeout));
    });
  }

  removeRTCListener() {
    if (this._peerConnection) {
      this._peerConnection.oniceconnectionstatechange = null;
      this._peerConnection.onconnectionstatechange = null;
      this._peerConnection.onsignalingstatechange = null;
      this._peerConnection.ontrack = null;
    }
    if (this.dtlsTransport) {
      this.dtlsTransport.onstatechange = null;
    }
  }

  requestRemoteFallbackToH264() {
    this._log.warn('H265 decode failed, remote need to fallback h264');
    this._h265DecodeFailed = true;
    this.signalChannelInstance
      .sendWaitForResponse({
        command: SignalCommand.UPDATE_CONSTRAINT_CONFIG,
        data: {
          videoDecCodec: 'h264'
        },
        responseCommand: SignalEvent.UPDATE_CONSTRAINT_CONFIG_RES
      })
      .then(res => {
        if (res.data.code !== 0) {
          this._log.warn(res.data.message);
        }
      });
  }
}


class TLVUnit {
  tag: number;
  len: number;
  data: ArrayBuffer;
  constructor(arraybuffer: ArrayBuffer) {
    const dw = new DataView(arraybuffer);
    // @ts-ignore
    this.tag = dw.getUint16();
    this.len = dw.getUint16(2);
    this.data = new Uint8Array(arraybuffer).slice(4, 2 + 2 + this.len).buffer;
  }
}

class DataChannelMessage {
  tinyId: string;
  data: ArrayBuffer;
  constructor(arraybuffer: ArrayBuffer) {
    const dw = new DataView(arraybuffer);
    let index = 0;
    const tlvUnitList = [];
    while (index < dw.byteLength) {
      const len = dw.getUint16(index + 2);
      const tlvUnit = new TLVUnit(new Uint8Array(arraybuffer).slice(index, index + 2 + 2 + len).buffer);
      tlvUnitList.push(tlvUnit);
      index += 2 + 2 + len;
    }

    const dataChunks: ArrayBuffer[] = [];
    tlvUnitList.forEach(tlvUnit => {
      if (tlvUnit.tag === 1) {
        this.tinyId = new TextDecoder().decode(tlvUnit.data);
      } else if (tlvUnit.tag === 2) {
        dataChunks.push(tlvUnit.data);
      }
    });
    // 合并所有 data 分片
    const totalLen = dataChunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
    const merged = new Uint8Array(totalLen);
    let offset = 0;
    dataChunks.forEach(chunk => {
      merged.set(new Uint8Array(chunk), offset);
      offset += chunk.byteLength;
    });
    this.data = merged.buffer;
  }
}


const ssrcSet = new Set();

export function getNewSSRC(): number {
  const ssrc = Math.floor(Math.random() * 2 ** 32);
  if (ssrcSet.has(ssrc)) {
    return getNewSSRC();
  }
  ssrcSet.add(ssrc);
  return ssrc;
}

export function buildSSRCGroup(): SSRCGroup {
  return {
    audioSsrc: getNewSSRC(),
    bigVideoSsrc: getNewSSRC(),
    bigVideoRtxSsrc: getNewSSRC(),
    auxVideoSsrc: getNewSSRC(),
    auxVideoRtxSsrc: getNewSSRC()
  };
}
